import { Navigate, Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getCaseStudyBySlug } from '../content/editorial';
import { EditorialContainer, TopSpacer, usePublicationMeta, useSharePage } from '../components/editorial/PublicationShared';

const CASE_STUDY_MEDIA: Record<
  string,
  {
    heroImage: string;
    heroAlt: string;
    inlineImage?: string;
    inlineAlt?: string;
    caption: string;
    inlineCaption?: string;
    surface: string;
    framelessHero?: boolean;
  }
> = {
  'golden-rule-dairy': {
    heroImage: '/images/grdPrimary.png',
    heroAlt: 'Golden Rule Dairy featured homepage view.',
    inlineImage: '/images/goldenruledairyhomepage.png',
    inlineAlt: 'Golden Rule Dairy website homepage.',
    caption: 'A cleaner storefront and tighter message hierarchy gave the dairy a more legible public face.',
    inlineCaption:
      'The project focused on clarity first so every retail and direct-order touchpoint could inherit the same structure.',
    surface:
      'radial-gradient(circle at top right, rgba(248, 201, 104, 0.18), transparent 42%), linear-gradient(135deg, rgba(243, 239, 232, 0.96), rgba(235, 227, 208, 0.94))',
    framelessHero: true,
  },
  'roshi-health': {
    heroImage: '/images/roshihealthhomepage.png',
    heroAlt: 'Roshi Health homepage.',
    inlineImage: '/images/roshihealthhomepage.png',
    inlineAlt: 'Roshi Health website homepage.',
    caption: 'The work was designed to make Roshi Health feel more coherent, credible, and easier to scale.',
    inlineCaption:
      'A stronger service system comes from consistency across identity, interface, and internal operating structure.',
    surface:
      'radial-gradient(circle at top right, rgba(139, 224, 201, 0.2), transparent 42%), linear-gradient(135deg, rgba(243, 239, 232, 0.96), rgba(225, 240, 236, 0.94))'
  }
};

const Page = styled.main`
  min-height: 100vh;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background-color: #efecf0;
  color: #101625;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    pointer-events: none;
    z-index: 0;
    background-image:
      linear-gradient(
        to bottom,
        rgba(239, 236, 240, 0) 40%,
        rgba(239, 236, 240, 0) 60%,
        rgba(239, 236, 240, 0.65) 80%,
        rgba(239, 236, 240, 1) 100%
      ),
      radial-gradient(
        circle at 50% 50%,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.45) 75%,
        rgba(0, 0, 0, 0.65) 100%
      ),
      linear-gradient(
        90deg,
        rgba(246, 245, 247, 0.98) 0%,
        rgba(246, 245, 247, 0.96) 34%,
        rgba(246, 245, 247, 0.78) 56%,
        rgba(246, 245, 247, 0.42) 74%,
        rgba(246, 245, 247, 0.16) 100%
      ),
      linear-gradient(180deg, #f6f5f7 0%, #eceaed 100%);
    background-repeat: no-repeat;
    background-position: center top;
    background-size: 100% 100%;
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.96) 16%,
      rgba(0, 0, 0, 0.82) 28%,
      rgba(0, 0, 0, 0.56) 38%,
      rgba(0, 0, 0, 0.28) 45%,
      rgba(0, 0, 0, 0.1) 48%,
      rgba(0, 0, 0, 0.02) 50%,
      rgba(0, 0, 0, 0) 54%
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.96) 16%,
      rgba(0, 0, 0, 0.82) 28%,
      rgba(0, 0, 0, 0.56) 38%,
      rgba(0, 0, 0, 0.28) 45%,
      rgba(0, 0, 0, 0.1) 48%,
      rgba(0, 0, 0, 0.02) 50%,
      rgba(0, 0, 0, 0) 54%
    );
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const HeroSection = styled.section`
  background: transparent;
  padding: 0 0 3.5rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: rgba(16, 22, 37, 0.72);
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 700;
  margin-bottom: 1rem;

  &:hover {
    color: #101625;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  color: rgba(16, 22, 37, 0.54);
  font-size: 0.76rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
`;

const MetaDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: rgba(16, 22, 37, 0.24);
`;

const HeroShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(320px, 0.82fr);
  gap: 2.5rem;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 1.8rem;
  }
`;

const HeroCopy = styled.div`
  min-width: 0;
`;

const HeroTitle = styled.h1`
  margin: 0;
  max-width: 10.5ch;
  font-family: var(--font-serif);
  font-size: clamp(2.55rem, 5vw, 5rem);
  line-height: 0.92;
  letter-spacing: -0.045em;
  color: #101625;
`;

const HeroDeck = styled.p`
  margin: 1.05rem 0 0;
  max-width: 42rem;
  font-size: 1.04rem;
  line-height: 1.85;
  color: rgba(16, 22, 37, 0.8);
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  flex-wrap: wrap;
  margin-top: 1.4rem;
`;

const ShareButton = styled.button`
  border: 1px solid rgba(16, 22, 37, 0.12);
  background: rgba(255, 255, 255, 0.46);
  color: #101625;
  border-radius: 999px;
  padding: 0.82rem 1.15rem;
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
`;

const ServicesLine = styled.div`
  margin-top: 1.35rem;
  padding-top: 0.95rem;
  border-top: 1px solid rgba(16, 22, 37, 0.12);
`;

const ServicesLabel = styled.p`
  margin: 0 0 0.45rem;
  color: rgba(16, 22, 37, 0.48);
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
`;

const ServicesValue = styled.p`
  margin: 0;
  color: rgba(16, 22, 37, 0.86);
  font-size: 0.98rem;
  line-height: 1.7;
`;

const HeroFigure = styled.figure<{ $surface: string }>`
  position: relative;
  display: grid;
  grid-template-rows: minmax(240px, 1fr) auto;
  gap: 1rem;
  margin: 0;
  min-height: 400px;
  padding: 1.35rem 1.35rem 1.2rem;
  background: ${({ $surface }) => $surface};
  border-radius: 32px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(16, 22, 37, 0.65) 1px, transparent 1px),
      linear-gradient(90deg, rgba(16, 22, 37, 0.65) 1px, transparent 1px);
    background-size: 34px 34px;
    opacity: 0.06;
    pointer-events: none;
  }
`;

const HeroFigureMedia = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
`;

const HeroImage = styled.img`
  width: min(100%, 540px);
  max-height: 290px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 20px 30px rgba(10, 17, 28, 0.12));
`;

const HeroCaption = styled.figcaption`
  position: relative;
  z-index: 1;
  margin: 0;
  color: rgba(16, 22, 37, 0.66);
  font-size: 0.92rem;
  line-height: 1.6;
`;

const HeroFigurePlain = styled.figure`
  display: grid;
  gap: 1rem;
  margin: 0;
  align-content: start;
`;

const HeroFigurePlainMedia = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroImagePlain = styled.img`
  width: min(100%, 620px);
  height: auto;
  object-fit: contain;
  display: block;
`;

const StatsStrip = styled.section`
  margin-top: 2rem;
  padding: 1.15rem 0 0;
  border-top: 1px solid rgba(16, 22, 37, 0.12);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  min-width: 0;
`;

const StatLabel = styled.p`
  margin: 0;
  color: rgba(16, 22, 37, 0.48);
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
`;

const StatValue = styled.p`
  margin: 0.38rem 0 0;
  color: #101625;
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 700;
`;

const BodySection = styled.section`
  background: transparent;
  padding: 0 0 5.8rem;
`;

const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 250px) minmax(0, 1fr);
  gap: 3rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Rail = styled.aside`
  align-self: start;
  position: sticky;
  top: 108px;

  @media (max-width: 980px) {
    position: static;
  }
`;

const RailBlock = styled.section`
  padding-top: 1rem;
  border-top: 1px solid rgba(16, 22, 37, 0.12);

  & + & {
    margin-top: 1.1rem;
  }
`;

const RailLabel = styled.p`
  margin: 0 0 0.65rem;
  color: rgba(16, 22, 37, 0.5);
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
`;

const RailText = styled.p`
  margin: 0;
  color: rgba(16, 22, 37, 0.84);
  font-size: 0.96rem;
  line-height: 1.7;
`;

const RailList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.65rem;
`;

const RailItem = styled.li`
  color: rgba(16, 22, 37, 0.84);
  font-size: 0.95rem;
  line-height: 1.55;
  font-weight: 700;
`;

const Story = styled.article`
  min-width: 0;
`;

const Standfirst = styled.p`
  margin: 0;
  max-width: 52rem;
  font-family: var(--font-serif);
  font-size: clamp(1.55rem, 2.7vw, 2.35rem);
  line-height: 1.34;
  letter-spacing: -0.02em;
  color: #162036;
`;

const StorySection = styled.section`
  max-width: 48rem;
  margin-top: 3rem;
  padding-top: 1.3rem;
  border-top: 1px solid rgba(16, 22, 37, 0.12);
`;

const SectionHeading = styled.h2`
  margin: 0 0 1rem;
  font-family: var(--font-serif);
  font-size: clamp(1.7rem, 2.25vw, 2.45rem);
  line-height: 1.04;
  letter-spacing: -0.03em;
  color: #101625;
`;

const SectionParagraph = styled.p`
  margin: 0;
  font-size: 1.06rem;
  line-height: 1.95;
  color: rgba(16, 22, 37, 0.84);

  & + & {
    margin-top: 1rem;
  }
`;

const BulletList = styled.ul`
  margin: 1.15rem 0 0;
  padding-left: 1.1rem;
  display: grid;
  gap: 0.7rem;
  max-width: 46rem;
`;

const BulletItem = styled.li`
  font-size: 1rem;
  line-height: 1.8;
  color: rgba(16, 22, 37, 0.84);
`;

const NarrativeBreak = styled.blockquote`
  margin: 2.8rem 0;
  padding: 0 0 0 1.35rem;
  max-width: 44rem;
  border-left: 3px solid rgba(224, 161, 94, 0.72);
`;

const NarrativeText = styled.p`
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(2rem, 3.15vw, 3.25rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  color: #101625;
`;

const NarrativeSource = styled.footer`
  margin-top: 0.9rem;
  color: rgba(16, 22, 37, 0.56);
  font-size: 0.88rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
`;

const InlineFigure = styled.figure<{ $accent: string }>`
  position: relative;
  margin: 2.6rem 0;
  max-width: 50rem;
  border-top: 1px solid rgba(16, 22, 37, 0.12);

  &::before {
    content: '';
    position: absolute;
    inset: 1rem 0 1rem;
    background:
      radial-gradient(circle at top right, ${({ $accent }) => `${$accent}25`}, transparent 54%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.42), rgba(255, 255, 255, 0.18));
    pointer-events: none;
  }
`;

const InlineImage = styled.img`
  position: relative;
  z-index: 1;
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 18px 28px rgba(10, 17, 28, 0.12));
`;

export default function CaseStudyPage() {
  const { slug } = useParams();
  const study = getCaseStudyBySlug(slug);
  const safeTitle = study?.client ?? 'Case Study';
  const safeDeck = study?.deck ?? 'CyBard client case study.';
  const safeShareTitle = study?.shareTitle ?? 'CyBard case study';

  usePublicationMeta(safeTitle, safeDeck);
  const { share, state } = useSharePage(safeShareTitle);

  if (!study) {
    return <Navigate to="/works" replace />;
  }

  const media = CASE_STUDY_MEDIA[study.slug];

  return (
    <Page>
      <TopSpacer />

      <HeroSection>
        <EditorialContainer>
          <BackLink to="/works">Back to works</BackLink>

          <HeroMeta>
            <span>{study.category}</span>
            <MetaDot />
            <span>{study.timeline}</span>
            <MetaDot />
            <span>{study.client}</span>
          </HeroMeta>

          <HeroShell>
            <HeroCopy>
              <HeroTitle>{study.client}</HeroTitle>
              <HeroDeck>{study.deck}</HeroDeck>

              <HeroActions>
                <ShareButton type="button" onClick={share}>
                  {state === 'copied' ? 'Link copied' : 'Share case study'}
                </ShareButton>
              </HeroActions>

              <ServicesLine>
                <ServicesLabel>Services engaged</ServicesLabel>
                <ServicesValue>{study.services.join(' / ')}</ServicesValue>
              </ServicesLine>
            </HeroCopy>

            {media?.framelessHero ? (
              <HeroFigurePlain>
                <HeroFigurePlainMedia>
                  <HeroImagePlain src={media.heroImage} alt={media.heroAlt} />
                </HeroFigurePlainMedia>
                <HeroCaption>{media.caption}</HeroCaption>
              </HeroFigurePlain>
            ) : media ? (
              <HeroFigure $surface={media.surface}>
                <HeroFigureMedia>
                  <HeroImage src={media.heroImage} alt={media.heroAlt} />
                </HeroFigureMedia>
                <HeroCaption>{media.caption}</HeroCaption>
              </HeroFigure>
            ) : null}
          </HeroShell>

          <StatsStrip>
            <StatsGrid>
              {study.stats.map((stat) => (
                <StatItem key={stat.label}>
                  <StatLabel>{stat.label}</StatLabel>
                  <StatValue>{stat.value}</StatValue>
                </StatItem>
              ))}
            </StatsGrid>
          </StatsStrip>
        </EditorialContainer>
      </HeroSection>

      <BodySection>
        <EditorialContainer>
          <BodyGrid>
            <Rail>
              <RailBlock>
                <RailLabel>Snapshot</RailLabel>
                <RailText>{study.summary}</RailText>
              </RailBlock>

              <RailBlock>
                <RailLabel>Services</RailLabel>
                <RailList>
                  {study.services.map((service) => (
                    <RailItem key={service}>{service}</RailItem>
                  ))}
                </RailList>
              </RailBlock>

              <RailBlock>
                <RailLabel>Timeline</RailLabel>
                <RailText>{study.timeline}</RailText>
              </RailBlock>
            </Rail>

            <Story>
              <Standfirst>{study.summary}</Standfirst>

              <NarrativeBreak>
                <NarrativeText>{study.deck}</NarrativeText>
                <NarrativeSource>{study.client}</NarrativeSource>
              </NarrativeBreak>

              {study.sections.map((section, index) => (
                <StorySection key={section.title}>
                  <SectionHeading>{section.title}</SectionHeading>

                  {section.paragraphs.map((paragraph) => (
                    <SectionParagraph key={paragraph}>{paragraph}</SectionParagraph>
                  ))}

                  {section.bullets?.length ? (
                    <BulletList>
                      {section.bullets.map((bullet) => (
                        <BulletItem key={bullet}>{bullet}</BulletItem>
                      ))}
                    </BulletList>
                  ) : null}

                  {index === 0 && media?.inlineImage ? (
                    <InlineFigure $accent={study.theme.accent}>
                      <InlineImage src={media.inlineImage} alt={media.inlineAlt ?? media.heroAlt} />
                    </InlineFigure>
                  ) : null}
                </StorySection>
              ))}
            </Story>
          </BodyGrid>
        </EditorialContainer>
      </BodySection>
    </Page>
  );
}
